(ns manage.ad-variant.creation.content.hooks
  (:require ["moment/min/moment-with-locales$default" :as moment]
            [cljs-bean.core :refer [->clj]]
            [common.queries :refer [use-query]]
            [common.queries.muix-filter-util :as filter-util]
            [common.resource.query.adset :as adset-queries]
            [manage.ad-variant.creation.content.columns :as c]
            [manage.ad-variant.hooks :as ad-variant-hooks]
            [manage.ad-variant.queries :as q]
            [manage.content-variant-page.queries :as content-queries]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn template-dimensions-available?
  [content-feed]
  (->> content-feed
       :data
       :field-classes
       (map :class)
       set
       (#(contains? % "template-dimensions-key"))))

(defn mapping-complete?
  [content-feed]
  (->> content-feed
       ((juxt :field-classes :field-content-mappings))
       (map count)
       (apply +)
       pos?))

(defhook use-content-data
  [{:keys [advertiser-id feed-id user-can-edit?]}]
  (let [;; Mapping type and placeholder mappings for the fields
        content-feed-response (use-query (content-queries/get-content-feed advertiser-id feed-id))

        ;; Find out the configured content fields for the table column definitions
        content-feed (:data content-feed-response)
        content-fields (uix/use-memo (fn []
                                       (c/content-fields content-feed))
                                     [content-feed])
        dimensions-available? (uix/use-memo (fn []
                                              (template-dimensions-available? content-feed))
                                            [content-feed])

        content-id-field-name (uix/use-memo (fn []
                                              (->> (:data content-feed)
                                                   :field-classes
                                                   (filter #(= (:class %) "identifier-key"))
                                                   first
                                                   :name
                                                   keyword))
                                            [content-feed])

        ;; Build the column defs
        columns (uix/use-memo (fn []
                                (-> [c/import-transaction-id-column
                                     c/row-number-column
                                     (c/id-column user-can-edit?)
                                     (when dimensions-available?
                                       c/template-dimensions-column)]
                                    (#(remove nil? %))
                                    vec
                                    (into (map #(c/content-column % user-can-edit?) content-fields))
                                    (conj c/last-modified-column)
                                    into-array))
                              [content-fields dimensions-available? user-can-edit?])
        field->resolver (->> columns
                             (keep (fn [^js column]
                                     (when-let [resolver (.-resolver column)]
                                       [(.-field column) resolver])))
                             (into {}))
        field->sort-field (->> columns
                               (keep (fn [^js column]
                                       (when-let [sort-field (.-sortField column)]
                                         [(.-field column) sort-field])))
                               (into {}))
        field->column (->> columns
                           (keep (fn [^js column]
                                   (when-let [column-name (.-column column)]
                                     [(.-field column) column-name])))
                           (into {}))

        table-query-fn (uix/use-callback (fn [table-models]
                                           (content-queries/get-content-variants
                                            advertiser-id feed-id
                                            {:field->resolver field->resolver
                                             :field->sort-field field->sort-field
                                             :field->column field->column
                                             :quick-filter {:field "content-id"}}
                                            table-models))
                                         [advertiser-id feed-id field->resolver field->sort-field field->column])

        table-filter-fn (uix/use-callback (fn [filter-model]
                                            (filter-util/get-filter-params
                                             {:field->resolver field->resolver
                                              :field->column field->column
                                              :quick-filter {:field "content-id"}}
                                             (->clj filter-model)))
                                          [field->resolver field->column])]
    {:columns columns
     :table-query-fn table-query-fn
     :table-filter-fn table-filter-fn
     :content-id-field-name content-id-field-name}))

(defhook use-latest-import
  [{:keys [advertiser-id feed-id]}]
  (let [latest-import-status-response (ad-variant-hooks/use-trigger-content-refetch {:advertiser-id advertiser-id :feed-id feed-id})
        import-state (get-in latest-import-status-response [:data :data :state])]
    {:import-state import-state}))

(defhook use-editing-lock [{:keys [advertiser-id feed-id]}]
  (let [{user-id :id} (use-subscribe [:resource.user/me])
        {inline-editing-lock :data} (use-query (content-queries/inline-editing-lock-query advertiser-id feed-id))
        {editing-user-id :useraccount-id editing-user-name :name} (get-in inline-editing-lock [:data :user])]
    {:user-is-editing? (= user-id editing-user-id)
     :user-can-start-editing? (nil? inline-editing-lock)
     :editing-user-name editing-user-name}))

(defhook use-can-safely-trigger-feed-import? [{:keys [advertiser-id feed-id]}]
  (let [{transactions :data} (use-query (content-queries/inline-editing-transactions advertiser-id feed-id))
        {{content-feed :data} :data} (use-query (content-queries/get-content-feed advertiser-id feed-id))
        {feed :data} (use-query (content-queries/get-feed advertiser-id feed-id))
        {{latest-import-data :data} :data} (use-query (content-queries/latest-feed-import-status-query advertiser-id feed-id))

        feed-updated-at (some-> (:updated-at feed) moment)
        content-feed-updated-at (some-> (:updated-at content-feed) moment)
        latest-transaction-created-at (some-> (first transactions) :created-at moment)
        latest-import-created-at (some-> (:created-at latest-import-data) moment)]
    ;; Feed was imported after the latest inline editing transaction
    (or (> latest-import-created-at latest-transaction-created-at)
        ;; OR placeholder mappings were updated after latest feed import
        (> content-feed-updated-at latest-import-created-at)
        ;; OR feed was updated after latest feed import
        (> feed-updated-at latest-import-created-at))))

(defhook use-unapplied-changes []
  (let [{:keys [advertiser-id adset-id] :as url-context} (use-subscribe [:url-context])
        {adset :data} (use-query (adset-queries/adset-query {:adset-id adset-id}))
        feed-id (:feedId adset)

        {transactions :data} (use-query (content-queries/inline-editing-transactions advertiser-id feed-id))
        {{content-feed :data} :data} (use-query (content-queries/get-content-feed advertiser-id feed-id))
        {feed :data} (use-query (content-queries/get-feed advertiser-id feed-id))
        {{latest-import-data :data} :data} (use-query (content-queries/latest-feed-import-status-query advertiser-id feed-id))
        {ad-builders :data} (use-query (q/ad-builders-upgrade-hints-query url-context))
        {{adset-content-build :data} :data} (use-query (q/adset-content-build-query url-context))

        ad-variants-updated-at (some-> (:latest-ad-variants-updated-at adset-content-build) moment)
        ad-builders-updated-at (some->> (seq ad-builders)
                                        (map (comp moment :updated-at))
                                        to-array
                                        (.max moment))
        feed-updated-at (some-> (:updated-at feed) moment)
        content-feed-updated-at (some-> (:updated-at content-feed) moment)
        latest-transaction-created-at (some-> (first transactions) :created-at moment)
        latest-import-created-at (some-> (:created-at latest-import-data) moment)]
    {:template (> ad-builders-updated-at ad-variants-updated-at)
     :feed (or (> feed-updated-at ad-variants-updated-at)
               (> latest-import-created-at ad-variants-updated-at))
     :placeholder-mapping (> content-feed-updated-at ad-variants-updated-at)
     :inline-edits (and (> latest-transaction-created-at ad-variants-updated-at)
                        (> latest-transaction-created-at latest-import-created-at))}))

(defhook use-setup-complete? []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {adset :data} (use-query (adset-queries/adset-query {:adset-id adset-id}))
        feed-id (:feedId adset)
        {ad-builders :data} (use-query (q/ad-builders-upgrade-hints-query {:adset-id adset-id}))

        {{content-feed :data} :data} (use-query (content-queries/get-content-feed advertiser-id feed-id))
        has-complete-mapping? (mapping-complete? content-feed)]
    {:template (seq ad-builders)
     :feed (or (some? feed-id) (some? content-feed))
     :placeholder-mapping has-complete-mapping?}))
